import React, { Fragment, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { 
  Box, Table, TableBody, TableCell, TableContainer, TableHead, 
  TableRow, Paper, Checkbox, FormControlLabel, Popover, Button, 
  Typography, MenuItem, Select, TextField, InputAdornment
} from "@mui/material";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SearchIcon from "@mui/icons-material/Search";
import { useHistory } from "react-router-dom";
import { connect } from "react-redux";
import { getValueByKey, displayDateTimeByLanguage } from "../../Utils/utils";
import SideBar from "../Sidebar/SideBar";
import SheetsPagePreviewCard from "./SheetsPagePreviewCard";
import NoSheets from "../NotFound/NoSheets";
import axios from "axios";
import "./SheetsPage.css";

import {
  getSheetPage,
  incrementSheetPage,
  setSheetPage,
  decrementSheetPage,
  setSheetSortOrder,
  setSheetViewMode,
  setSheetPageSize,
  resetData
} from "../../Redux/Actions/dataActions";


function SheetsPage({
  getSheetPage, sheetPages, incrementSheetPage, setSheetPage, decrementSheetPage,
  sheetPage, sheetSortOrder, sheetViewMode, setSheetSortOrder, totalSheetPages,
  setSheetViewMode, entrysPerSheetPage, setSheetPageSize, userData, dispatch, resetData, props 
  }) {
	  
  const { t } = useTranslation();
  const userLang = userData?.language || 'de';
  const [sheetsOptions, setSheetsOptions] = useState([]);

  useEffect(() => {
    if (!userLang) return;

    // Definition der parallelen Requests
    const fetchSongtype           = axios.get(`/settings/options?type=songtype&lang=${userLang}`);
    const fetchClassification     = axios.get(`/settings/options?type=classification&lang=${userLang}`);
    const fetchInstrument         = axios.get(`/settings/options?type=instrument&lang=${userLang}`);

    // Promise.all feuert beide Abfragen zeitgleich ab (schneller!)
    Promise.all([fetchSongtype, fetchClassification, fetchInstrument])
      .then(([songtypeRes, classificationRes, instrumentRes]) => {
        const songtypeData = songtypeRes.data || [];
        const classificationData = classificationRes.data || [];
        const instrumentData = instrumentRes.data || [];

        // 👍 Hier werden beide Arrays zu einem einzigen flachen Array zusammengemischt
        const combinedOptions = [...songtypeData, ...classificationData, ...instrumentData];

        setSheetsOptions(combinedOptions);
        console.log("✅ Optionen erfolgreich gemischt im Speicher:", combinedOptions);
      })
      .catch(err => {
        console.error("Fehler beim parallelen Laden der Dropdown-Optionen:", err);
      });

  }, [userLang]); 


	// ⭐ MASTER-DEFINITION: Auf reines CamelCase umgestellt
	const FIELD_DEFINITIONS = {
	  sheetName: { 
		label: t("common.song_title"), type: "text", 
		sort: (a, b, asc) => asc ? (a.sheetName || "").localeCompare(b.sheetName || "") : (b.sheetName || "").localeCompare(a.sheetName || "") 
	  },
	  composer: { 
		label: t("common.composer"), type: "text", 
		sort: (a, b, asc) => asc ? (a.composer || "").localeCompare(b.composer || "") : (b.composer || "").localeCompare(a.composer || "") 
	  },
	  category: { 
		label: t("sheet.category"), type: "dropdown", optionType: "songtype", 
		sort: (a, b, asc) => asc ? (a.category || "").localeCompare(b.category || "") : (b.category || "").localeCompare(a.category || "") 
	  },
	  keySignature: { 
		label:t("sheet.keySignature"), type: "text", 
		sort: (a, b, asc) => asc ? (a.keySignature || "").localeCompare(b.keySignature || "") : (b.keySignature || "").localeCompare(a.keySignature || "") 
	  },
	  tempo: { 
		label: t("sheet.tempo"), type: "int", 
		sort: (a, b, asc) => asc ? (a.tempo || 0) - (b.tempo || 0) : (b.tempo || 0) - (a.tempo || 0) 
	  },
	  duration: { 
		label: t("sheet.duration"), type: "time",
		sort: (a, b, asc) => asc ? (a.duration || "").localeCompare(b.duration || "") : (b.duration || "").localeCompare(a.duration || "") 
	  },
	  collection: { 
		label: t("sheet.collection"), type: "text", 
		sort: (a, b, asc) => asc ? (a.collection || "").localeCompare(b.collection || "") : (b.collection || "").localeCompare(a.collection || "") 
	  },
	  instrument: { 
		label: t("sheet.instrument"), type: "dropdown", optionType: "instrument", 
		sort: (a, b, asc) => asc ? (a.instrument || "").localeCompare(b.instrument || "") : (b.instrument || "").localeCompare(a.instrument || "") 
	  },  
	  ratingAvg: { 
		label: t("sheet.ratingAvg"), type: "float", 
		sort: (a, b, asc) => {const valA = a.ratingAvg || 0; const valB = b.ratingAvg || 0; return asc ? valA - valB : valB - valA; } 
	  },  
	  updatedAt: { 
		label: t("common.updatedAt"), type: "datetime",
		sort: (a, b, asc) => asc ? new Date(a.updatedAt) - new Date(b.updatedAt) : new Date(b.updatedAt) - new Date(a.updatedAt) 
	  }
	};

  const [loading, setLoading] = useState(true);
  const [selectedLetter, setSelectedLetter] = useState("Alle");
  const [anchorEl, setAnchorOpen] = useState(null);
  const history = useHistory();

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  // Erkennt nun CamelCase aus dem LocalStorage
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const localUser = JSON.parse(localStorage.getItem("user"));
    if (localUser && localUser.sheetColumns) {
      try {
        const parsed = JSON.parse(localUser.sheetColumns);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch (e) {}
    }
    return ["sheetName", "composer", "category"]; // Sicherer CamelCase Fallback
  });
  
  useEffect(() => {
    if (userData && userData.id) {
      axios.get(`/users/info/${userData.id}`)
        .then((res) => {
          if (res.data) {
            if (res.data.sheetSort) {
              setSheetSortOrder(res.data.sheetSort);
            }
            if (res.data.sheetColumns) {
              try {
                const parsed = JSON.parse(res.data.sheetColumns);
                if (Array.isArray(parsed) && parsed.length > 0) {
                  setVisibleColumns(parsed); 
                }
              } catch (e) {}
            }
          }
        })
        .catch(err => console.error("Fehler beim Live-Layout-Abruf:", err));
    }
  }, [userData, setSheetSortOrder]);

  const alphabet = [t("common.all"), ..."ABCDEFGHIJKLMNOPQRSTUVWXYZ"];

  const getData = useCallback(() => {
    if (sheetPage === undefined || sheetPage < 1) setSheetPage(1);
    const data = { page: sheetPage, sortBy: "updatedAt desc" }; 
    setLoading(true);
    getSheetPage(data, () => setLoading(false));
  }, [sheetPage, getSheetPage, setSheetPage]);

  useEffect(() => {
    getData();
    document.title = `SheetAble - ${t("PageSheetsPage.pageTitle")}`;
  }, [getData, t]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (debouncedQuery.trim() === "") {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    axios.get(`/search/${encodeURIComponent(debouncedQuery.trim())}`)
      .then((res) => {
        if (res.data) setSearchResults(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Fehler bei der Live-Suche:", err);
        setLoading(false);
      });
  }, [debouncedQuery]);

  const saveLayoutToDatabase = (newSort, newCols) => {
    if (!userData || !userData.id) return;
    
    const finalSort = newSort || sheetSortOrder;
    const finalCols = newCols || visibleColumns;

    const payload = {
      sheetSort: finalSort,
      sheetColumns: JSON.stringify(finalCols)
    };

    axios.put(`/users/layout/${userData.id}`, payload)
      .then((res) => {
        if (dispatch) dispatch({ type: "SET_USER_DATA", payload: res.data });
        localStorage.setItem("user", JSON.stringify(res.data));
      })
      .catch(err => console.error("Sync-Fehler bei Sheets:", err));
  };

  const handleColumnToggle = (columnId) => {
    let updatedCols = visibleColumns.includes(columnId)
      ? visibleColumns.filter(id => id !== columnId)
      : [...visibleColumns, columnId];
    
    if (updatedCols.length === 0) return; 

    let finalSort = sheetSortOrder;
    if (!updatedCols.some(id => sheetSortOrder.startsWith(id))) {
      finalSort = updatedCols[0] + "_asc";
      setSheetSortOrder(finalSort);
    }

    setVisibleColumns(updatedCols);

    if (dispatch) {
      dispatch({
        type: "SET_USER_DATA",
        payload: {
          ...userData,
          sheetSort: finalSort,
          sheetColumns: JSON.stringify(updatedCols)
        }
      });
    }

    saveLayoutToDatabase(finalSort, updatedCols);
  };

  const handleHeaderClick = (columnId) => {
    let newSortOrder = `${columnId}_asc`;
    if (sheetSortOrder === `${columnId}_asc`) {
      newSortOrder = `${columnId}_desc`;
    } else if (sheetSortOrder === `${columnId}_desc`) {
      newSortOrder = `${columnId}_asc`;
    }
    setSheetSortOrder(newSortOrder);
    saveLayoutToDatabase(newSortOrder, visibleColumns);
  };

  const dynamicSortOptions = [];
  visibleColumns.forEach((colId) => {
    if (FIELD_DEFINITIONS[colId]) {
      dynamicSortOptions.push({ value: `${colId}_asc`, label: `${FIELD_DEFINITIONS[colId].label} (${t("common.asc")})` });
      dynamicSortOptions.push({ value: `${colId}_desc`, label: `${FIELD_DEFINITIONS[colId].label} (${t("common.desc")})` });
    }
  });

  const sourceSheets = debouncedQuery.trim() !== "" 
    ? searchResults 
    : (sheetPages && sheetPages[sheetPage] ? sheetPages[sheetPage].filter(Boolean) : []);

  const existingLetters = new Set(
    sourceSheets
      .map((s) => s.sheetName ? s.sheetName.trim().charAt(0).toUpperCase() : "")
      .filter(Boolean)
  );

  const filteredSheets = selectedLetter === t("common.all")
    ? sourceSheets
    : sourceSheets.filter((s) => s.sheetName && s.sheetName.toUpperCase().startsWith(selectedLetter));
    
  let sortedSheets = [...filteredSheets];
  let activeSortOpt = dynamicSortOptions.find(opt => opt.value === sheetSortOrder);
  
  if (!activeSortOpt && dynamicSortOptions.length > 0) {
    activeSortOpt = dynamicSortOptions[0];
  }

  if (activeSortOpt) {
    const isAsc = activeSortOpt.value.endsWith("_asc");
    const activeFieldId = activeSortOpt.value.replace(isAsc ? "_asc" : "_desc", "");
    if (FIELD_DEFINITIONS[activeFieldId]) {
      sortedSheets.sort((a, b) => FIELD_DEFINITIONS[activeFieldId].sort(a, b, isAsc));
    }
  }

   // 4. Dynamische Gesamtseiten basierend auf den gefilterten Daten und der Seitengröße berechnen
  const calculatedTotalPages = Math.ceil(sortedSheets.length / (entrysPerSheetPage || 10)) || 1;

  // Sicherheitscheck: Falls die aktuelle Seite durch Filtern/Skalieren außerhalb des Bereichs liegt
  useEffect(() => {
    if (sheetPage > calculatedTotalPages) {
      setSheetPage(calculatedTotalPages);
    }
  }, [calculatedTotalPages, sheetPage, setSheetPage]);

  const startIndex = (sheetPage - 1) * entrysPerSheetPage;
  const endIndex = startIndex + entrysPerSheetPage;

  const paginatedSheets = sortedSheets.slice(startIndex, endIndex);

  // Modifizierte Pagination-Handler, die sich nach dem neuen Limit richten
  const handlePrevPage = (e) => {
    e.preventDefault();
    if (sheetPage > 1) {
      decrementSheetPage();
    }
  };

  const handleNextPage = (e) => {
    e.preventDefault();
    if (sheetPage < calculatedTotalPages) {
      incrementSheetPage();
    }
  };

  const hasData = paginatedSheets.length > 0;

  return (
    <Fragment>
      <SideBar />
      <div id={hasData ? "" : "notfound"} className="home_content">
        {!loading ? (
          hasData ? (
            <div className="sheets-wrapper" style={{ padding: "20px" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3, flexWrap: "wrap", gap: "15px" }}>
                <Box display="flex" alignItems="center" gap="15px">
                  <span className="doc_sheet">{t("PageSheetsPage.pageTitle")}</span>
                  <Typography variant="body2" sx={{ backgroundColor: "rgba(79, 64, 179, 0.08)", color: "#4f40b3", fontWeight: "600", px: 1.5, py: 0.5, borderRadius: "12px", fontSize: "13px" }}>
                    {sortedSheets.length} {sortedSheets.length === 1 ? t("common.entry") : t("common.entries") }
                  </Typography>
                </Box>
                
                <TextField
                  id="permanent-sheet-search-field"
                  key="global-sheet-search-input"
                  size="small"
                  variant="outlined"
                  placeholder={t("PageSheetsPage.placeholder_search")}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  style={{ width: "320px", backgroundColor: "white", borderRadius: "8px" }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: "#4f40b3" }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <Box display="flex" gap="12px" alignItems="center">
                  {debouncedQuery.trim() === "" && (
                    <Box display="flex" alignItems="center" gap="6px">
                      <Typography variant="caption" sx={{ color: "#757575", fontWeight: "600" }}>{t("common.entries")}:</Typography>
                      <Select
                        size="small"
                        value={entrysPerSheetPage || 10}
                        onChange={(e) => {
                          const newSize = parseInt(e.target.value, 10);
                          setSheetPageSize(newSize); 
                          setSheetPage(1); 
                          saveLayoutToDatabase(sheetSortOrder, visibleColumns);
                        }}
                        sx={{ fontSize: "13px", backgroundColor: "white", height: "32px" }}
                        MenuProps={{
						style: { zIndex: 99999 }, // Falls es hinter Fenstern liegt
						disablePortal: false,
						MenuListProps: {
						  style: { 
							display: "flex", 
							flexDirection: "column", // Zwingt das innere ul-Layout untereinander
							padding: "4px 0"
						  }
						}
					  }}>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                      </Select>
                    </Box>
                  )}

                  {sheetViewMode === "list" && (
                    <Fragment>
                      <Button variant="outlined" startIcon={<ViewColumnIcon />} onClick={(e) => setAnchorOpen(e.currentTarget)} sx={{ textTransform: "none", color: "#4f40b3", borderColor: "#4f40b3", height: "32px" }}>
                        {t("common.column")}
                      </Button>
                      <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorOpen(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: "6px", minWidth: "180px" }}>
                          <Typography variant="caption" sx={{ fontWeight: "bold", mb: 1, color: "#4f40b3" }}>{t("common.column")}:</Typography>
                          {Object.keys(FIELD_DEFINITIONS).map((id) => (
                            <FormControlLabel 
                              key={id} 
                              control={<Checkbox size="small" checked={visibleColumns.includes(id)} onChange={() => handleColumnToggle(id)} sx={{ '&.Mui-checked': { color: '#4f40b3' } }} />} 
                              label={<Typography variant="body2" style={{ color: "#1d1b31" }}>{FIELD_DEFINITIONS[id].label}</Typography>} 
                            />
                          ))}
                        </Box>
                      </Popover>
                    </Fragment>
                  )}

                  <Select native size="small" value={dynamicSortOptions.some(o => o.value === sheetSortOrder) ? sheetSortOrder : ""} onChange={(e) => { setSheetSortOrder(e.target.value); saveLayoutToDatabase(e.target.value, visibleColumns); }} sx={{ fontSize: "14px", backgroundColor: "white", paddingRight: "10px", height: "32px" }}>
                    {dynamicSortOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </Select>

                  <Select size="small" value={sheetViewMode} onChange={(e) => setSheetViewMode(e.target.value)} 
				  sx={{ fontSize: "14px", backgroundColor: "white", height: "32px" }} 
				    MenuProps={{
						style: { zIndex: 99999 }, // Falls es hinter Fenstern liegt
						disablePortal: false,
						MenuListProps: {
						  style: { 
							display: "flex", 
							flexDirection: "column", // Zwingt das innere ul-Layout untereinander
							padding: "4px 0"
						  }
						}
					  }}>
                    <MenuItem value="gallery">{t("common.view_mode_gallery")}</MenuItem>
                    <MenuItem value="list">{t("common.view_mode_list")}</MenuItem>
                  </Select>
                </Box>
              </Box>

              <Box display="flex" flexWrap="wrap" gap="4px" sx={{ mb: 3 }}>
                {alphabet.map(l => {
                  const isAvailable = l === t("common.all") || existingLetters.has(l);
                  return (
                    <Button 
                      key={l} 
                      variant={selectedLetter === l ? "contained" : "text"} 
                      size="small" 
                      disabled={!isAvailable}
                      onClick={() => setSelectedLetter(l)} 
                      sx={{ 
                        minWidth: "30px", 
                        height: "32px", 
                        backgroundColor: selectedLetter === l ? "#4f40b3" : "transparent", 
                        color: selectedLetter === l ? "white" : (isAvailable ? "#1d1b31" : "#c0c0c0"),
                        fontWeight: isAvailable ? "600" : "normal",
                        '&.Mui-disabled': { color: '#d0d0d0', backgroundColor: 'transparent' }
                      }}
                    >
                      {l}
                    </Button>
                  );
                })}
              </Box>
			  
              {sheetViewMode === "gallery" ? (
                <div className="middle-part-container">
                  <ul className="all-sheets-container full-width" style={{ display: "flex", flexWrap: "wrap", gap: "20px", padding: 0, margin: 0, listStyle: "none" }}>
                    {paginatedSheets.map(s => (
                      <SheetsPagePreviewCard key={`gallery-${s.safeSheetName}-${s.id}`} sheet={s} />
                    ))}
                  </ul>
                </div>
              ) : (
                <TableContainer component={Paper} sx={{ borderRadius: "12px", border: "1px solid #ececec" }}>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: "#fcfbfe" }}>
                      <TableRow>
                        {Object.keys(FIELD_DEFINITIONS).filter(id => visibleColumns.includes(id)).map(id => {
                          const isSortedThisField = sheetSortOrder.startsWith(id);
                          const isAsc = sheetSortOrder.endsWith("_asc");
                          return (
                            <TableCell 
                              key={id} 
                              onClick={() => handleHeaderClick(id)}
                              sx={{ 
                                fontWeight: "bold", color: "#4f40b3", py: 1.5, cursor: "pointer", userSelect: "none",
                                '&:hover': { backgroundColor: "rgba(79, 64, 179, 0.04)" }
                              }}
                            >
                              <Box display="flex" alignItems="center" gap="4px">
                                {FIELD_DEFINITIONS[id].label}
                                {isSortedThisField && (
                                  isAsc ? <ArrowUpwardIcon style={{ fontSize: "14px" }} /> : <ArrowDownwardIcon style={{ fontSize: "14px" }} />
                                )}
                              </Box>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedSheets.map((s) => (
                        <TableRow 
                          key={`row-${s.safeSheetName}-${s.id}`} 
                          hover 
                          onClick={() => {
                            if (s.pdfUrl) {
                              history.push(`/sheet/${s.pdfUrl.split("pdf/").pop()}`);
                            }
                          }} 
                          style={{ cursor: "pointer" }}
                        >
                          {Object.keys(FIELD_DEFINITIONS).filter(id => visibleColumns.includes(id)).map(id => (
                            <TableCell key={`cell-${id}-${s.id}`} sx={{ py: 1.5 }}>
                              {id === "sheetName" ? ( <b>{s.sheetName}</b> ) : 
							    id === "updatedAt" ? displayDateTimeByLanguage(s.updatedAt, userLang) : 
								  FIELD_DEFINITIONS[id].type === "dropdown" ? getValueByKey(sheetsOptions, s[id], userLang, FIELD_DEFINITIONS[id].optionType) :
                                s[id] || "-"
                              }
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {debouncedQuery.trim() === "" && (
                <Box display="flex" justifyContent="center" alignItems="center" gap="15px" sx={{ mt: 4 }}>
                  <Button variant="outlined" size="small" disabled={sheetPage <= 1} onClick={handlePrevPage}>&lt;</Button>
					<Typography variant="body2">{t("common.pageCount", {startCnt: sheetPage, endCnt: calculatedTotalPages})}</Typography>
                  <Button variant="outlined" size="small" disabled={sheetPage >= calculatedTotalPages} onClick={handleNextPage}>&gt;</Button>
                </Box>
              )}
            </div>
          ) : <NoSheets />
        ) : <div className="loading">{t("PageSheetsPage.loading_page")}</div>}
      </div>
    </Fragment>
  );
}

const mapStateToProps = (state) => ({
  sheetPages: state.data.sheetPages,
  sheetPage: state.data.sheetPage,
  totalSheetPages: state.data.totalSheetPages,
  sheetSortOrder: state.data.sheetSortOrder,
  sheetViewMode: state.data.sheetViewMode,
  entrysPerSheetPage: state.data.entrysPerSheetPage,
  userData: state.user.userData, 
});

const mapActionsToProps = { 
  getSheetPage, 
  incrementSheetPage, 
  setSheetPage, 
  decrementSheetPage, 
  setSheetSortOrder, 
  setSheetViewMode, 
  setSheetPageSize,
  resetData, 
};

const ConnectedSheetsPage = connect(mapStateToProps, mapActionsToProps)(SheetsPage);
export default ConnectedSheetsPage;
